/**
 * Site → GTM dataLayer helpers.
 * See docs/gtm-tracking.md for setup and how to add tracking.
 *
 * All interactive helpers return a thunk — call it inside a Vue method:
 *   gtmTracking("CTA Click", "Try free", "Hero")()
 * Never invoke in a template attribute (that runs on render).
 */

const PAGE_DEPTH_KEY = "gtm_page_depth";
const EXPLICIT_EVENTS = ["custom_click", "RegistrationStart", "Register", "submit"];
const CTA_LABEL_PATTERN =
  /try free|get started|book demo|contact sales|start free trial|sign up/i;
const LANDMARK_SELECTOR =
  ".hero, header, .header, .banner, nav, footer, .cta, .footer-cta, section, article, aside, main";

/** Title-case words for gtm_type / gtm_context so casing does not fragment reports. */
export function capitalizeWords(str) {
  if (typeof str !== "string") return str;
  return str.replace(/\S+/g, (word) => word.charAt(0).toUpperCase() + word.slice(1));
}

export function isBrowser() {
  return typeof window !== "undefined" && typeof document !== "undefined";
}

/** Push a raw payload to window.dataLayer (creates the array if needed). */
export function pushDataLayerEvent(payload) {
  if (!isBrowser() || !payload) return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(payload);
}

/**
 * Generic click. Event: custom_click.
 * @param {string} type - e.g. "CTA Click", "Link Click"
 * @param {string} label - stable English identifier for the control
 * @param {string} [context=""] - e.g. "Hero", "Footer"
 * @returns {() => void}
 */
export function gtmTracking(type, label, context = "") {
  return () => {
    pushDataLayerEvent({
      event: "custom_click",
      gtm_type: capitalizeWords(type),
      gtm_label: label,
      gtm_context: capitalizeWords(context),
    });
  };
}

/**
 * User started registration (opened app register, or submitted SaasRise form).
 * Event: RegistrationStart — pairs with completed Register.
 * Not for links that only go to /pricing.
 * Same arg order as gtmTracking: (type, label, context).
 * @param {string} type
 * @param {string} [label=""]
 * @param {string} [context=""]
 * @returns {() => void}
 */
export function gtmRegistrationStart(type, label = "", context = "") {
  return () => {
    pushDataLayerEvent({
      event: "RegistrationStart",
      gtm_type: capitalizeWords(type),
      gtm_label: label,
      gtm_context: capitalizeWords(context),
    });
  };
}

/**
 * Account created successfully.
 * Event: Register — same name as the app completion event.
 * On marketing, only SaasRise pushes this (signup happens on-page).
 * Normal Pricing/Blog CTAs leave Register to the app.
 * @param {string} type
 * @param {string} [label=""]
 * @param {string} [context=""]
 * @returns {() => void}
 */
export function gtmRegister(type, label = "", context = "") {
  return () => {
    pushDataLayerEvent({
      event: "Register",
      gtm_type: capitalizeWords(type),
      gtm_label: label,
      gtm_context: capitalizeWords(context),
    });
  };
}

/**
 * Form submit (non-register). Event: submit.
 * @param {string} type
 * @param {string} label
 * @param {string} [context=""]
 * @returns {() => void}
 */
export function gtmSubmit(type, label, context = "") {
  return () => {
    pushDataLayerEvent({
      event: "submit",
      gtm_type: capitalizeWords(type),
      gtm_label: label,
      gtm_context: capitalizeWords(context),
    });
  };
}

/**
 * Scroll depth milestone. Event: scroll.
 * @param {number} percent
 * @returns {() => void}
 */
export function gtmScrollTracking(percent) {
  return () => {
    pushDataLayerEvent({
      event: "scroll",
      gtm_percent_scrolled: percent,
    });
  };
}

/** Increment session page depth and push page_depth. Call on route changes. */
export function trackPageDepth() {
  if (!isBrowser()) return;

  try {
    const current = parseInt(sessionStorage.getItem(PAGE_DEPTH_KEY) || "0", 10);
    const next = (Number.isFinite(current) ? current : 0) + 1;
    sessionStorage.setItem(PAGE_DEPTH_KEY, String(next));
    pushDataLayerEvent({
      event: "page_depth",
      gtm_page_depth: next,
    });
  } catch (e) {
    // sessionStorage can be blocked
  }
}

/**
 * Listen for scroll and fire each threshold once. Returns a cleanup function.
 * Re-attach on each route change so thresholds reset per page view.
 * @param {number[]} [percentages=[25, 50, 75, 90]]
 * @returns {() => void}
 */
export function setupScrollTracking(percentages = [25, 50, 75, 90]) {
  if (!isBrowser()) return () => {};

  const fired = new Set();

  const getPercentScrolled = () => {
    const scrollTop =
      window.scrollY ||
      document.documentElement.scrollTop ||
      document.body.scrollTop ||
      0;
    const docHeight =
      document.documentElement.scrollHeight - window.innerHeight;
    if (docHeight <= 0) return 0;
    return (scrollTop / docHeight) * 100;
  };

  const onScroll = () => {
    const percent = getPercentScrolled();
    percentages.forEach((threshold) => {
      if (!fired.has(threshold) && percent >= threshold) {
        fired.add(threshold);
        gtmScrollTracking(threshold)();
      }
    });
  };

  window.addEventListener("scroll", onScroll, { passive: true });
  requestAnimationFrame(() => {
    requestAnimationFrame(onScroll);
  });

  return () => {
    window.removeEventListener("scroll", onScroll);
  };
}

function normalizeText(value) {
  if (!value || typeof value !== "string") return "";
  return value.replace(/\s+/g, " ").trim();
}

function getElementClassName(el) {
  if (!el) return "";
  const raw = el.className;
  if (!raw) return "";
  if (typeof raw === "string") return raw;
  if (typeof raw.baseVal === "string") return raw.baseVal;
  return "";
}

function getTrackingLabel(el) {
  const dataLabel = normalizeText(el.getAttribute && el.getAttribute("data-gtm-label"));
  if (dataLabel) return dataLabel;

  const ariaLabel = normalizeText(el.getAttribute && el.getAttribute("aria-label"));
  if (ariaLabel) return ariaLabel;

  const heading = el.querySelector && el.querySelector(".mega-menu__title, h5, h6, h3");
  if (heading) {
    const headingText = normalizeText(heading.innerText || heading.textContent);
    if (headingText) return headingText;
  }

  const text = normalizeText(el.innerText || el.textContent);
  if (text) return text;

  const title = normalizeText(el.getAttribute && el.getAttribute("title"));
  if (title) return title;

  const href = el.getAttribute && el.getAttribute("href");
  if (href) return href;

  if (el.id) return el.id;

  const className = normalizeText(getElementClassName(el));
  if (className) return className;

  return "Unknown";
}

function getMeaningfulClass(el) {
  const className = getElementClassName(el);
  if (!className) return "";
  return (
    className
      .split(/\s+/)
      .find((token) => token && !token.startsWith("data-v-")) || ""
  );
}

function getTrackingContext(el) {
  const withAttr = el.closest("[data-gtm-context]");
  if (withAttr) {
    return withAttr.getAttribute("data-gtm-context") || "";
  }

  const landmark = el.closest(LANDMARK_SELECTOR);
  if (landmark) {
    const classToken = getMeaningfulClass(landmark);
    if (classToken) return classToken;

    const tag = landmark.tagName.toLowerCase();
    if (tag === "header") return "Header";
    if (tag === "footer") return "Footer";
    if (tag === "nav") return "Navigation";
    if (tag === "main") return "Main Content";
  }

  if (el.closest("header")) return "Header";
  if (el.closest("footer")) return "Footer";
  if (el.closest("nav")) return "Navigation";
  if (el.closest("main")) return "Main Content";

  return isBrowser() ? window.location.pathname : "";
}

const DOWNLOAD_EXTENSION_PATTERN =
  /\.(pdf|xlsx?|docx?|pptx?|zip|csv|txt)(?:[?#]|$)/i;

function isDownloadLink(el) {
  if (!el || !el.getAttribute) return false;
  if (el.hasAttribute("download")) return true;
  const href = el.getAttribute("href") || "";
  return DOWNLOAD_EXTENSION_PATTERN.test(href);
}

function getTrackingType(el, label) {
  if (el.closest("nav.nav-container, .mega-menu")) return "Top Nav Click";
  if (el.closest("footer.footer") && !el.closest(".footer-cta")) return "Footer Click";
  if (isDownloadLink(el)) return "Download";

  const className = getElementClassName(el).toLowerCase();
  const isCtaClass = /\bbtn\b|\bcta\b/.test(className);
  const isCtaLabel = CTA_LABEL_PATTERN.test(label || "");

  if (isCtaClass || isCtaLabel) return "CTA Click";
  if (el.tagName && el.tagName.toLowerCase() === "a") return "Link Click";
  return "Button Click";
}

/**
 * Document-level click fallback for a[href] and button.
 * Skips if an explicit custom_click / RegistrationStart / Register / submit was already pushed.
 * Idempotent. Returns a cleanup function.
 * @returns {() => void}
 */
export function setupGlobalClickTracking() {
  if (!isBrowser()) return () => {};
  if (window.__globalClickTrackingSetup) return () => {};

  const onClick = (event) => {
    const el = event.target && event.target.closest
      ? event.target.closest("a[href], button")
      : null;
    if (!el) return;
    if (el.closest('[data-gtm-ignore="true"]')) return;

    window.dataLayer = window.dataLayer || [];
    const startLength = window.dataLayer.length;

    // Microtask (not setTimeout): runs after bubble-phase Vue handlers, but
    // still before navigation unload so full-page clicks are not dropped.
    queueMicrotask(() => {
      const newEntries = window.dataLayer.slice(startLength);
      const alreadyTracked = newEntries.some(
        (entry) => entry && EXPLICIT_EVENTS.includes(entry.event)
      );
      if (alreadyTracked) return;

      const label = getTrackingLabel(el);
      const type = getTrackingType(el, label);
      let context = "";
      if (type === "Top Nav Click") {
        const mega = el.closest(".mega-menu[data-gtm-context]");
        context = mega ? mega.getAttribute("data-gtm-context") || "" : "";
      } else if (type !== "Footer Click") {
        context = getTrackingContext(el);
      }
      gtmTracking(type, label, context)();
    });
  };

  // Capture so we snapshot dataLayer.length before Vue click handlers push.
  document.addEventListener("click", onClick, true);
  window.__globalClickTrackingSetup = true;

  return () => {
    document.removeEventListener("click", onClick, true);
    window.__globalClickTrackingSetup = false;
  };
}
